// src/pages/CreateServicePage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";

/* antd */
import {
  Layout,
  Card,
  Row,
  Col,
  Space,
  Typography,
  Tag,
  Menu as AntMenu,
  Button,
  Table,
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Popconfirm,
  Alert,
  Breadcrumb,
  Divider,
  Badge,
  message,
  Tooltip,
  Segmented,
} from "antd";

/* icons */
import {
  AppstoreOutlined,
  ProfileOutlined,
  UnorderedListOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  HomeOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const { Content } = Layout;
const { Text } = Typography;

/* utils */
const asNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

// Add custom CSS for slim design
const slimTableStyles = `
  .slim-table .ant-table-thead > tr > th {
    padding: 8px 8px !important;
    font-size: 12px;
    font-weight: 600;
    background-color: #fafafa;
  }
  
  .slim-table .ant-table-tbody > tr > td {
    padding: 8px 8px !important;
    font-size: 13px;
  }
  
  .slim-table .ant-btn-sm {
    height: 24px;
    padding: 0 8px;
    font-size: 12px;
    border-radius: 4px;
  }
  
  .slim-table .ant-tag {
    margin: 0;
    font-size: 12px;
    padding: 1px 6px;
    border-radius: 4px;
  }
  
  .slim-table .ant-space {
    gap: 4px !important;
  }
  
  .slim-table .ant-table-pagination {
    margin: 16px 0 !important;
  }
`;

const CreateServicePage = () => {
  const navigate = useNavigate();
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  // Add custom styles to document head
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = slimTableStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [activeTab, setActiveTab] = useState("services"); // 'services' | 'menu' | 'types'
  const [error, setError] = useState("");

  /* ------------ Services ------------- */
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  const selectedService = useMemo(
    () => services.find((s) => s.serviceId === selectedServiceId) || null,
    [services, selectedServiceId]
  );

  // Service modal (create/edit)
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceModalSaving, setServiceModalSaving] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm] = Form.useForm();

  /* ------------ Menu Items ------------- */
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Item modal (create/edit)
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemModalSaving, setItemModalSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm] = Form.useForm();

  /* ------------ Service Types ------------- */
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // Type modal (create/edit)
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [typeModalSaving, setTypeModalSaving] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm] = Form.useForm();

  /* ------------ Info modal ------------- */
  const [infoOpen, setInfoOpen] = useState(false);

  const hasSelectedService = useMemo(() => !!selectedServiceId, [selectedServiceId]);

  /* ------------ Loaders ------------- */
  const loadServices = useCallback(async () => {
    if (!hotelId) return;
    setServicesLoading(true);
    try {
      const res = await axiosInstance.get(`/Services/by-hotel/${hotelId}`, {
        params: { hotelId },
        validateStatus: (s) => [200, 204, 404].includes(s),
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setServices(list);
      if (!selectedServiceId && list.length) {
        setSelectedServiceId(list[0].serviceId);
      }
      setError("");
    } catch (e) {
      setServices([]);
      setError(e?.response?.data?.message || "Failed to load services.");
    } finally {
      setServicesLoading(false);
    }
  }, [hotelId, selectedServiceId]);

  const loadItems = useCallback(async (sid) => {
    if (!sid) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      const res = await axiosInstance.get(`/MenuItems/by-service/${sid}`, {
        params: { hotelId },
        validateStatus: (s) => [200, 204, 404].includes(s),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItems([]);
      message.error(e?.response?.data?.message || "Failed to load menu items.");
    } finally {
      setItemsLoading(false);
    }
  }, [hotelId]);

  const loadTypes = useCallback(
    async (sid) => {
      if (!sid) {
        setTypes([]);
        return;
      }
      setTypesLoading(true);
      try {
        const res = await axiosInstance.get(`/RoomServiceTypes/by-service/${sid}`, {
          params: { hotelId },
          validateStatus: (s) => [200, 204, 404].includes(s),
        });
        setTypes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setTypes([]);
        message.error(e?.response?.data?.message || "Failed to load service types.");
      } finally {
        setTypesLoading(false);
      }
    },
    [hotelId]
  );

  useEffect(() => {
    if (!hotelId) {
      setError("Hotel ID is missing from auth.");
      return;
    }
    loadServices();
  }, [hotelId, loadServices]);

  useEffect(() => {
    if (selectedServiceId) {
      loadItems(selectedServiceId);
      loadTypes(selectedServiceId);
    } else {
      setItems([]);
      setTypes([]);
    }
  }, [selectedServiceId, loadItems, loadTypes]);

  /* ------------ Service: modal handlers ------------- */
  const openNewService = () => {
    setEditingService(null);
    serviceForm.resetFields();
    serviceForm.setFieldsValue({ name: "", description: "", isActive: true });
    setServiceModalOpen(true);
  };

  const openEditService = (record) => {
    setEditingService(record);
    serviceForm.setFieldsValue({
      name: record.name || "",
      description: record.description || "",
      isActive: !!record.isActive,
    });
    setServiceModalOpen(true);
  };

  const saveService = async () => {
    if (!hotelId) return;
    try {
      const vals = await serviceForm.validateFields();
      setServiceModalSaving(true);

      const payload = {
        hotelId,
        name: vals.name,
        description: vals.description || "",
        isActive: !!vals.isActive,
      };

      if (editingService?.serviceId) {
        await axiosInstance.put(`/Services/${editingService.serviceId}`, payload, {
          params: { hotelId }
        });
        message.success("Service updated.");
      } else {
        const res = await axiosInstance.post(`/Services`, payload, {
          params: { hotelId }
        });
        const created = res.data || {};
        setSelectedServiceId(created.serviceId || created.id);
        message.success("Service created.");
      }

      setServiceModalOpen(false);
      setEditingService(null);
      await loadServices();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.message || "Failed to save service.");
    } finally {
      setServiceModalSaving(false);
    }
  };

  const deleteService = async (record) => {
    try {
      await axiosInstance.delete(`/Services/${record.serviceId}`, {
        params: { hotelId }
      });
      message.success("Service deleted.");
      if (selectedServiceId === record.serviceId) {
        const next = services.filter((s) => s.serviceId !== record.serviceId);
        setSelectedServiceId(next[0]?.serviceId ?? null);
      }
      await loadServices();
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete service.");
    }
  };

  /* ------------ Menu Item: modal handlers ------------- */
  const openNewItem = () => {
    if (!hasSelectedService) {
      message.info("Please select a service first.");
      return;
    }
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      serviceId: selectedServiceId,
      category: "",
      name: "",
      description: "",
      price: null,
      estimatedReadyTime: null,
      isActive: true,
    });
    setItemModalOpen(true);
  };

  const openEditItem = (record) => {
    setEditingItem(record);
    itemForm.setFieldsValue({
      serviceId: record.serviceId,
      category: record.category || "",
      name: record.name || "",
      description: record.description || "",
      price: asNum(record.price) || 0,
      estimatedReadyTime: record.estimatedReadyTime ?? null,
      isActive: !!record.isActive,
    });
    setItemModalOpen(true);
  };

  const saveItem = async () => {
    const vals = await itemForm.validateFields().catch((e) => e);
    if (vals?.errorFields) return;

    const svcId = Number(vals.serviceId || selectedServiceId);
    if (!svcId) {
      message.error("Please pick a service.");
      return;
    }

    try {
      setItemModalSaving(true);
      const payload = {
        hotelId,
        serviceId: svcId,
        category: vals.category || null,
        name: vals.name,
        description: vals.description || null,
        price: asNum(vals.price),
        estimatedReadyTime:
          vals.estimatedReadyTime != null ? asNum(vals.estimatedReadyTime) : null,
        isActive: !!vals.isActive,
      };

      if (editingItem?.menuItemId) {
        await axiosInstance.put(`/MenuItems/${editingItem.menuItemId}`, payload, {
          params: { hotelId }
        });
        message.success("Item updated.");
      } else {
        await axiosInstance.post(`/MenuItems`, payload, {
          params: { hotelId }
        });
        message.success("Item added.");
      }

      setItemModalOpen(false);
      setEditingItem(null);
      if (svcId !== selectedServiceId) setSelectedServiceId(svcId);
      await loadItems(svcId);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save item.");
    } finally {
      setItemModalSaving(false);
    }
  };

  const deleteItem = async (record) => {
    try {
      await axiosInstance.delete(`/MenuItems/${record.menuItemId}`, {
        params: { hotelId }
      });
      message.success("Item deleted.");
      await loadItems(selectedServiceId);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete item.");
    }
  };

  /* ------------ Service Type: modal handlers ------------- */
  const openNewType = () => {
    if (!hasSelectedService) {
      message.info("Please select a service first.");
      return;
    }
    setEditingType(null);
    typeForm.resetFields();
    typeForm.setFieldsValue({
      serviceId: selectedServiceId,
      name: "",
      category: "",
      description: "",
      estimatedMinutes: null,
      isActive: true,
    });
    setTypeModalOpen(true);
  };

  const openEditType = (record) => {
    setEditingType(record);
    typeForm.setFieldsValue({
      serviceId: record.serviceId,
      name: record.name || "",
      category: record.category || "",
      description: record.description || "",
      estimatedMinutes: record.estimatedMinutes ?? null,
      isActive: !!record.isActive,
    });
    setTypeModalOpen(true);
  };

  const saveType = async () => {
    const vals = await typeForm.validateFields().catch((e) => e);
    if (vals?.errorFields) return;

    const svcId = Number(vals.serviceId || selectedServiceId);
    if (!svcId) {
      message.error("Please pick a service.");
      return;
    }

    try {
      setTypeModalSaving(true);
      const payload = {
        hotelId,
        serviceId: svcId,
        name: vals.name,
        category: vals.category || null,
        description: vals.description || null,
        estimatedMinutes:
          vals.estimatedMinutes != null ? asNum(vals.estimatedMinutes) : null,
        isActive: !!vals.isActive,
      };

      if (editingType?.roomServiceTypeId) {
        await axiosInstance.put(`/RoomServiceTypes/${editingType.roomServiceTypeId}`, payload, {
          params: { hotelId }
        });
        message.success("Service type updated.");
      } else {
        await axiosInstance.post(`/RoomServiceTypes`, payload, {
          params: { hotelId }
        });
        message.success("Service type added.");
      }

      setTypeModalOpen(false);
      setEditingType(null);
      if (svcId !== selectedServiceId) setSelectedServiceId(svcId);
      await loadTypes(svcId);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to save service type.");
    } finally {
      setTypeModalSaving(false);
    }
  };

  const deleteType = async (record) => {
    try {
      await axiosInstance.delete(`/RoomServiceTypes/${record.roomServiceTypeId}`, {
        params: { hotelId }
      });
      message.success("Service type deleted.");
      await loadTypes(selectedServiceId);
    } catch (e) {
      message.error(e?.response?.data?.message || "Failed to delete service type.");
    }
  };

  /* ------------ Tables ------------- */
  const serviceColumns = [
    { 
      title: "Name", 
      dataIndex: "name",
      width: 160,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      width: 200,
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Actions",
      dataIndex: "_actions",
      width: 140,
      render: (_, row) => (
        <Space size={6} wrap>
          <Tooltip title="Edit service">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditService(row)} />
          </Tooltip>
          <Tooltip title="Delete service">
            <Popconfirm title="Delete service?" onConfirm={() => deleteService(row)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const itemColumns = [
    { 
      title: "#", 
      width: 50, 
      render: (_v, _r, i) => <Text type="secondary">{i + 1}</Text> 
    },
    { 
      title: "Category", 
      dataIndex: "category",
      width: 120,
    },
    { 
      title: "Name", 
      dataIndex: "name",
      width: 140,
    },
    {
      title: "Price",
      dataIndex: "price",
      width: 90,
      render: (v) => (v != null ? `$${Number(v).toFixed(2)}` : "-"),
    },
    {
      title: "ETA (min)",
      dataIndex: "estimatedReadyTime",
      width: 100,
      render: (v) => (v != null ? v : "-"),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Actions",
      dataIndex: "_actions",
      width: 120,
      render: (_, row) => (
        <Space size={6} wrap>
          <Tooltip title="Edit item">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditItem(row)} />
          </Tooltip>
          <Tooltip title="Delete item">
            <Popconfirm title="Delete item?" onConfirm={() => deleteItem(row)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const typeColumns = [
    { 
      title: "#", 
      width: 50, 
      render: (_v, _r, i) => <Text type="secondary">{i + 1}</Text> 
    },
    { 
      title: "Name", 
      dataIndex: "name",
      width: 140,
    },
    { 
      title: "Category", 
      dataIndex: "category", 
      width: 120, 
      render: (v) => v || "-" 
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      width: 160,
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: "Est. Minutes",
      dataIndex: "estimatedMinutes",
      width: 110,
      render: (v) => (v != null ? v : "-"),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Actions",
      dataIndex: "_actions",
      width: 120,
      render: (_, row) => (
        <Space size={6} wrap>
          <Tooltip title="Edit type">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditType(row)} />
          </Tooltip>
          <Tooltip title="Delete type">
            <Popconfirm title="Delete service type?" onConfirm={() => deleteType(row)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  /* ------------ Render ------------- */
  if (!hotelId) {
    return (
      <div style={{ margin: "48px auto" }}>
        <Alert
          type="error"
          showIcon
          message="Hotel ID missing"
          description="Hotel ID was not found in auth data. Please sign in again."
        />
      </div>
    );
  }

  const breadcrumbItems = [
    { title: <Link to="/dashboard"><HomeOutlined /> Home</Link> },
    { title: "Service Manager" },
  ];

  return (
    <Layout style={{ background: "transparent", width: "100%" }}>
      <Content style={{ width: "100%", margin: 0, padding: 16 }}>
         

        <Card
          title={
            <Space>
              <Button type="text" size="small" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
              <Text strong>Service, Menu &amp; Service Types</Text>
              <Badge count={services.length} showZero style={{ backgroundColor: "#1677ff" }} />
              <Button type="text" size="small" icon={<InfoCircleOutlined />} onClick={() => setInfoOpen(true)} />
            </Space>
          }
          extra={
            <Space>
              <Tag color="blue">Hotel #{hotelId}</Tag>
              <Button 
                size="small"
                icon={<ReloadOutlined />} 
                onClick={loadServices} 
                loading={servicesLoading}
              >
                Refresh
              </Button>
            </Space>
          }
          variant="outlined"
          bodyStyle={{ paddingTop: 8, paddingBottom: 8 }}
        >
          {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

          {/* Slim Segmented Control */}
          <div style={{ overflowX: "auto", paddingBottom: 4 }}>
            <Segmented
              options={[
                { 
                  value: "services", 
                  label: (
                    <Space size={6}>
                      <AppstoreOutlined />
                      <span>Services</span>
                      <Badge size="small" count={services.length} />
                    </Space>
                  ) 
                },
                { 
                  value: "menu", 
                  label: (
                    <Space size={6}>
                      <ProfileOutlined />
                      <span>Menu</span>
                      <Badge size="small" count={items.length} />
                    </Space>
                  ) 
                },
                { 
                  value: "types", 
                  label: (
                    <Space size={6}>
                      <UnorderedListOutlined />
                      <span>Service Types</span>
                      <Badge size="small" count={types.length} />
                    </Space>
                  ) 
                },
              ]}
              value={activeTab}
              onChange={(v) => setActiveTab(v)}
              size="large"
            />
          </div>

          <div style={{ marginTop: 16 }}>
            {/* SERVICES TAB */}
            {activeTab === "services" && (
              <Card
                title="Services"
                extra={
                  <Space>
                    <Button 
                      type="primary" 
                      size="small"
                      icon={<PlusOutlined />} 
                      onClick={openNewService}
                    >
                      New Service
                    </Button>
                  </Space>
                }
                variant="outlined"
                bodyStyle={{ paddingBottom: 8 }}
              >
                <Table
                  className="slim-table"
                  rowKey="serviceId"
                  dataSource={services}
                  columns={serviceColumns}
                  loading={servicesLoading}
                  pagination={{ pageSize: 10 }}
                  locale={{ emptyText: "No services yet." }}
                  tableLayout="fixed"
                  scroll={{ x: 600 }}
                />
              </Card>
            )}

            {/* MENU TAB */}
            {activeTab === "menu" && (
              <Card
                title={
                  <Space wrap>
                    <span>Menu</span>
                    <Text type="secondary">for</Text>
                    <Select
                      size="small"
                      placeholder={servicesLoading ? "Loading…" : "Select service"}
                      value={selectedServiceId ?? undefined}
                      onChange={(v) => setSelectedServiceId(v ?? null)}
                      loading={servicesLoading}
                      options={(services || []).map((s) => ({
                        value: s.serviceId,
                        label: s.name,
                      }))}
                      style={{ width: 200 }}
                    />
                  </Space>
                }
                extra={
                  <Space>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => loadItems(selectedServiceId)}
                      disabled={!hasSelectedService}
                    >
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={openNewItem}
                      disabled={!hasSelectedService}
                    >
                      New Item
                    </Button>
                  </Space>
                }
                variant="outlined"
                bodyStyle={{ paddingBottom: 8 }}
              >
                <Table
                  className="slim-table"
                  size="middle"
                  rowKey="menuItemId"
                  dataSource={items}
                  columns={itemColumns}
                  loading={itemsLoading}
                  pagination={{ pageSize: 10 }}
                  locale={{
                    emptyText: hasSelectedService
                      ? "No menu items yet."
                      : "Select a service to view its menu.",
                  }}
                  tableLayout="fixed"
                  scroll={{ x: 700 }}
                />
              </Card>
            )}

            {/* SERVICE TYPES TAB */}
            {activeTab === "types" && (
              <Card
                title={
                  <Space wrap>
                    <span>Service Types</span>
                    <Text type="secondary">for</Text>
                    <Select
                      size="small"
                      placeholder={servicesLoading ? "Loading…" : "Select service"}
                      value={selectedServiceId ?? undefined}
                      onChange={(v) => setSelectedServiceId(v ?? null)}
                      loading={servicesLoading}
                      options={(services || []).map((s) => ({
                        value: s.serviceId,
                        label: s.name,
                      }))}
                      style={{ width: 200 }}
                    />
                  </Space>
                }
                extra={
                  <Space>
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={() => loadTypes(selectedServiceId)}
                      disabled={!hasSelectedService}
                    >
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={openNewType}
                      disabled={!hasSelectedService}
                    >
                      New Type
                    </Button>
                  </Space>
                }
                variant="outlined"
                bodyStyle={{ paddingBottom: 8 }}
              >
                <Table
                  className="slim-table"
                  size="middle"
                  rowKey={(r) => r.roomServiceTypeId ?? r.id}
                  dataSource={types}
                  columns={typeColumns}
                  loading={typesLoading}
                  pagination={{ pageSize: 10 }}
                  locale={{
                    emptyText: hasSelectedService
                      ? "No service types yet."
                      : "Select a service to view types.",
                  }}
                  tableLayout="fixed"
                  scroll={{ x: 700 }}
                />
              </Card>
            )}
          </div>
        </Card>
      </Content>

      {/* Service Modal */}
      <Modal
        open={serviceModalOpen}
        title={editingService ? "Edit Service" : "New Service"}
        onCancel={() => {
          setServiceModalOpen(false);
          setEditingService(null);
        }}
        onOk={saveService}
        okText={editingService ? "Save Changes" : "Create Service"}
        confirmLoading={serviceModalSaving}
        destroyOnClose
        width={500}
      >
        <Form
          form={serviceForm}
          layout="vertical"
          initialValues={{ name: "", description: "", isActive: true }}
        >
          <Form.Item
            label="Service Name"
            name="name"
            rules={[{ required: true, message: "Please enter service name" }]}
          >
            <Input placeholder="e.g., Housekeeping, Room Service, Spa, Laundry" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Describe the service" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Menu Item Modal */}
      <Modal
        open={itemModalOpen}
        title={
          editingItem
            ? "Edit Menu Item"
            : `New Menu Item${selectedService ? ` — ${selectedService.name}` : ""}`
        }
        onCancel={() => {
          setItemModalOpen(false);
          setEditingItem(null);
        }}
        onOk={saveItem}
        okText={editingItem ? "Save Changes" : "Add Item"}
        confirmLoading={itemModalSaving}
        destroyOnClose
        width={500}
      >
        <Form
          form={itemForm}
          layout="vertical"
          initialValues={{
            serviceId: selectedServiceId,
            category: "",
            name: "",
            description: "",
            price: null,
            estimatedReadyTime: null,
            isActive: true,
          }}
        >
          <Form.Item
            label="Service"
            name="serviceId"
            rules={[{ required: true, message: "Please choose a service" }]}
          >
            <Select
              placeholder="Select a service (e.g., Kitchen, Bar)"
              options={(services || []).map((s) => ({
                value: s.serviceId,
                label: s.name,
              }))}
            />
          </Form.Item>

          <Form.Item label="Category" name="category">
            <Input placeholder="e.g., Food / Beverage" />
          </Form.Item>
          <Form.Item
            label="Item Name"
            name="name"
            rules={[{ required: true, message: "Item name is required" }]}
          >
            <Input placeholder="Item name" />
          </Form.Item>
          <Form.Item
            label="Price"
            name="price"
            rules={[{ required: true, message: "Price is required" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.01} placeholder="0.00" />
          </Form.Item>
          <Form.Item label="Estimated Ready Time (min)" name="estimatedReadyTime">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Service Type Modal */}
      <Modal
        open={typeModalOpen}
        title={
          editingType
            ? "Edit Service Type"
            : `New Service Type${selectedService ? ` — ${selectedService.name}` : ""}`
        }
        onCancel={() => {
          setTypeModalOpen(false);
          setEditingType(null);
        }}
        onOk={saveType}
        okText={editingType ? "Save Changes" : "Add Service Type"}
        confirmLoading={typeModalSaving}
        destroyOnClose
        width={500}
      >
        <Form
          form={typeForm}
          layout="vertical"
          initialValues={{
            serviceId: selectedServiceId,
            name: "",
            category: "",
            description: "",
            estimatedMinutes: null,
            isActive: true,
          }}
        >
          <Form.Item
            label="Service"
            name="serviceId"
            rules={[{ required: true, message: "Please choose a service" }]}
          >
            <Select
              placeholder="Select a service"
              options={(services || []).map((s) => ({
                value: s.serviceId,
                label: s.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Type Name"
            name="name"
            rules={[{ required: true, message: "Type name is required" }]}
          >
            <Input placeholder="e.g., Extra towels, Make up room" />
          </Form.Item>

          <Form.Item label="Category" name="category">
            <Input placeholder="e.g., Linen, Cleaning" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Form.Item label="Estimated Minutes" name="estimatedMinutes">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Info / Help Modal */}
      <Modal
        open={infoOpen}
        onCancel={() => setInfoOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setInfoOpen(false)}>
            Close
          </Button>,
        ]}
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Services, Menu & Service Types — Guide</Text>
          </Space>
        }
        width={640}
      >
        <div style={{ padding: "8px 0" }}>
          <Text>
            Manage your hotel's <strong>services</strong> (e.g., Housekeeping, Spa),
            their <strong>menu items</strong> (for sellable items), and standardized
            <strong> service types</strong> (common request templates like "Extra towels").
          </Text>

          <Divider orientation="left" style={{ margin: "16px 0" }}>
            How it works
          </Divider>
          <ul style={{ paddingLeft: 20 }}>
            <li><Text strong>Services tab:</Text> add/edit the parent services for your hotel.</li>
            <li><Text strong>Menu tab:</Text> pick a service and manage sellable menu items.</li>
            <li><Text strong>Service Types tab:</Text> define common request types (not tied to a room/booking).</li>
            <li><Text strong>Tenant-safe:</Text> your <code>hotelId</code> is included on creates/updates.</li>
          </ul>
        </div>
      </Modal>
    </Layout>
  );
};

export default CreateServicePage;